import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import {
  Truck,
  Trash,
  Users,
  Wrench,
  Leaf
} from "lucide-react";

import patric1 from "@/assets/schober/patric-1.webp";
import patric1Small from "@/assets/schober/patric-1-800.webp";
import patric2 from "@/assets/schober/patric-2.webp";
import team from "@/assets/schober/team.webp";
import { ContactForm } from "@/components/site/ContactForm";
import { services } from "@/data/services";
import { ServiceBlock } from "@/components/site/ServiceBlock";
import { Hero } from "@/components/site/Hero";
import { Testimonials } from "@/components/site/Testimonials";
import homebg from "@/assets/schober/home-bg.webp";
import heroImg from "@/assets/schober/hero.webp";
import heroImgMobile from "@/assets/schober/hero-800.webp";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Umzug & Entrümpelung Wien, Wr. Neustadt, Baden | Schober" },
      {
        name: "description",
        content:
          "Umzug, Transport & Entrümpelung in Wien, Niederösterreich (Wr. Neustadt, Baden, Neunkirchen, Mödling) und Burgenland (Eisenstadt, Mattersburg). Versichert, persönlich, faire Preise. ☎ +43 680 230 87 00.",
      },
      { property: "og:title", content: "Umzug & Entrümpelung Wien, Wr. Neustadt, Baden | Schober" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        property: "og:description",
        content: "Angebot-Umzüge, Transporte & Entrümpelungen in Wien, NÖ und Burgenland – von Wr. Neustadt und Baden bis Eisenstadt und Mattersburg.",
      },
    ],
    links: [
      {
        rel: "preload",
        as: "image",
        href: heroImg,
        imageSrcSet: `${heroImgMobile} 800w, ${heroImg} 1600w`,
        imageSizes: "100vw",
        fetchPriority: "high",
      } as any,
    ],
  }),
  component: Index,
});

const leistungen = [
  {
    text: "Transporte & Umzüge",
    icon: <Truck size={40} className="text-white" />,
  },
  {
    text: "Entrümpelungen & Sperrgut-Abtransport",
    icon: <Trash size={40} className="text-white" />,
  },
  {
    text: "Räumungen von Messie-Wohnungen",
    icon: <Users size={40} className="text-white" />,
  },
  {
    text: "Demontagearbeiten",
    icon: <Wrench size={40} className="text-white" />,
  },
  {
    text: "Grünschnitt-Abtransport",
    icon: <Leaf size={40} className="text-white" />,
  },
];

const homeAreas = [
  { ort: "wien", name: "Wien" },
  { ort: "wiener-neustadt", name: "Wiener Neustadt" },
  { ort: "baden", name: "Baden" },
  { ort: "neunkirchen", name: "Neunkirchen" },
  { ort: "moedling", name: "Mödling" },
  { ort: "st-poelten", name: "St. Pölten" },
  { ort: "eisenstadt", name: "Eisenstadt" },
  { ort: "mattersburg", name: "Mattersburg" },
];
const homeServices = [
  { slug: "umzug", label: "Umzug" },
  { slug: "entruempelung", label: "Entrümpelung" },
  { slug: "wohnungsaufloesung", label: "Wohnungsauflösung" },
  { slug: "firmenumzug", label: "Firmenumzug" },
];

function Index() {
  return (
    <>
      <Hero />

      {/* Kontaktformular-Card unter dem Slider */}
      <section className="relative bg-muted">
        <div className="container mx-auto max-w-7xl px-4 py-10">
          <div className="flex gap-10 flex-col md:flex-row">
            <div className="w-full md:w-[50%] flex gap-2 flex-col justify-center align-center">
              <h2 className="text-[2rem] md:text-[3rem] font-black text-foreground">Schober Transportservice</h2>
              <p className="text-[1.6rem] md:text-[2.5rem] mb-2 md:mb-5 font-black text-brand-orange">Wir packen an – we tackle it!</p>
              <p className="mx-auto mt-6 max-w-3xl leading-relaxed text-muted-foreground">Schober Transportservice – Ihr vertrauenswürdiger Partner für umfassende Dienstleistungen rund um Transport, Entrümpelungen und Räumungen. Mit einem fundierten Verständnis für die Bedeutung von Effizienz, Zuverlässigkeit und Kompetenz bieten wir eine breite Palette von Services an, die Ihr Leben leichter machen. Von der professionellen Entrümpelung über die Räumung von Messie-Wohnungen und -Wohnhäusern bis hin zu Umzügen und Demontagearbeiten – wir stehen für Schnelligkeit und Präzision in jeder unserer Aufgaben.</p>


            </div>
            <div className="relative w-full  md:w-[50%] z-20 mt-0 md:-mt-24 max-w-xl">
              <div className="rounded-0 bg-brand-navy p-8 shadow-2xl">
                <h2 className="text-center font-display text-2xl font-bold text-white">
                  Kontaktieren Sie uns!
                </h2>
                <div className="mt-5">
                  <ContactForm variant="dark" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    

      {/* Einsatzgebiet – interne Links zu den Ostregion-Seiten */}
      <section className="bg-background py-12 md:py-16">
        <div className="container mx-auto max-w-7xl px-4">
          <h2 className="font-display text-2xl font-extrabold text-brand-navy md:text-3xl">
            Umzug & Entrümpelung in Wien, Wiener Neustadt, Baden und Umgebung
          </h2>
          <p className="mt-3 max-w-3xl text-muted-foreground">
            Von unserem Standort in Bad Fischau-Brunn sind wir in kurzer Zeit in Wien, im Bezirk Wiener Neustadt, im Bezirk Baden, in Neunkirchen, Mödling, St. Pölten sowie in Eisenstadt und Mattersburg. Wählen Sie Ihren Ort:
          </p>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {homeAreas.map((a) => (
              <div key={a.ort} className="rounded-lg bg-muted p-5">
                <h3 className="font-display text-lg font-bold text-brand-navy">{a.name}</h3>
                <ul className="mt-2 space-y-1 text-sm">
                  {homeServices.map((s) => (
                    <li key={s.slug}>
                      <Link to="/leistungen/$service/$ort" params={{ service: s.slug, ort: a.ort }} className="text-foreground/80 hover:text-brand-orange hover:underline">
                        {s.label} {a.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Unsere Leistungen – Liste */}
      <section className="bg-muted py-12 md:py-16">
        <div className="container mx-auto max-w-7xl px-4">
          <h2 className="font-display text-2xl font-extrabold text-brand-navy md:text-3xl">
            Fernumzüge von und nach Wien, Wiener Neustadt und Umgebung
          </h2>
          <p className="mt-4 max-w-3xl leading-relaxed text-muted-foreground">
            Zum Studium nach Salzburg, für einen neuen Job nach Tirol oder mit der Familie nach Kärnten:
            Wir helfen auch bei Umzügen über die Region hinaus. Ob Ihr Umzug in Wien, Wiener Neustadt,
            Baden oder Neunkirchen startet oder Sie aus einem anderen Bundesland hierherziehen –
            Schober Transportservice übernimmt den Transport Ihrer Möbel und Umzugskartons.
          </p>
          <p className="mt-3 max-w-3xl leading-relaxed text-muted-foreground">
            Bei längeren Strecken stimmen wir Beladung, Fahrt und Anlieferung auf Ihre beiden Adressen
            und den Übergabetermin ab. Für Ihr individuelles Angebot benötigen wir Start- und Zieladresse,
            den gewünschten Zeitraum, die Menge des Umzugsguts sowie Angaben zu Stockwerk, Lift und Zufahrt.
          </p>
          <Link to="/leistungen/$service" params={{ service: "umzug" }} className="mt-6 inline-flex items-center gap-2 font-semibold text-brand-navy hover:underline">
            Umzug und Fernumzug anfragen <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Unsere Leistungen – Liste */}
      <section className="bg-muted py-10 md:py-40" style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.5) 100%), url(${homebg})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="container max-w-8xl mx-auto px-4">
          <div className="flex justify-space-between items-center flex-col md:flex-row gap-10">
          <div className="mb-10 text-center">
            <h2 className="text-[40px] font-black text-white mb-3">
              Unsere Leistungen
            </h2>
            
          </div>
          <ul className="mx-auto grid max-w-4xl md:max-w-5xl gap-3 bg-[rgba(27,42,71,0.8)] p-10 sm:grid-cols-3">
            {leistungen.map((s) => (
              <li
                key={s.text}
                className="flex items-center gap-3 p-4"
              >
                {s.icon}
                <span className="font-xl text-xl text-white">{s.text}</span>
              </li>
            ))}
          </ul>
          </div>
        </div>
      </section>

      {/* Service-Blöcke mit Original-Icons */}
      <section className="container-fluid mx-auto w-full py-10 md:py-20 border-b-2 border-brand-orange">
       <div className="flex flex-col md:grid gap-20 grid-cols-6 max-w-7xl mx-5 md:mx-auto">
         {services.map((s, i) => (
          <ServiceBlock
            key={s.title}
            image={s.image}
            title={s.title}
            text={s.text}
            index={i}
          />
        ))}
       </div>
      </section>

      {/* Vertrauen & Sicherheit – Patric-1 */}
      <section className="py-5 md:py-20">
        <div className="container mx-auto max-w-7xl grid gap-12 px-4 lg:grid-cols-2 lg:items-center">
          <div className="overflow-hidden rounded-0 shadow-xl">
            <img
              src={patric1Small}
              srcSet={`${patric1Small} 800w, ${patric1} 1200w`}
              sizes="(min-width: 1024px) 600px, 100vw"
              alt="Patric Schober"
              loading="lazy"
              width={1200}
              height={842}
              className="h-full w-full object-cover"
            />
          </div>
          <div>
            <h2 className="font-display text-2xl font-extrabold text-brand-navy md:text-3xl">
              Vertrauen & Sicherheit sind für mich Voraussetzung!
            </h2>
            <p className="mt-5 leading-relaxed text-muted-foreground">
              Durch meine langjährige Erfahrung als Polizist weiß ich, wie wichtig
              Vertrauen und Sicherheit sind. Dies sind mitunter die wichtigsten
              Faktoren für eine erfolgreiche Zusammenarbeit. Besonders, wenn es um
              Ihr Eigentum und die eigenen vier Wände geht, zählen diese Werte
              noch viel mehr. Der kompetente Umgang mit fremdem Hab und Gut sowie
              mit Wohnungen/Liegenschaften/Verlassenschaften stehen daher im
              Mittelpunkt unseres Handelns bei Schober Transportservice. Wir
              setzen auf Integrität und Seriosität, dubiose oder zweifelhafte
              Aufträge lehnen wir ab und bieten auch keine solchen selbst an. Bei
              Schober Transportservice legen wir Wert auf eine verlässliche
              Partnerschaft, die auf den Grundpfeilern von Vertrauen und
              Sicherheit aufbaut.
            </p>

          </div>
        </div>
      </section>

      {/* Vertrauen & Sicherheit – Patric-1 */}
      <section className="py-5 md:py-20">
        <div className="container max-w-7xl mx-auto grid gap-12 px-4 lg:grid-cols-2 lg:items-center">

          <div className="order-2 md:order-1">
           <h2 className="font-display text-2xl font-extrabold text-brand-navy md:text-3xl">
              Individuelle Angebote – faire Preise!
            </h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              Um eine transparente und faire Geschäftspraxis zu gewährleisten,
              bieten wir keine voreiligen Kostenvoranschläge an, ohne zuvor eine
              kostenfreie Besichtigung Ihres Objektes durchgeführt zu haben. So
              können wir sicherstellen, dass unsere Dienstleistungen genau auf
              Ihre individuellen Bedürfnisse zugeschnitten sind. Jedes Projekt
              ist einzigartig, und erst vor Ort kann der tatsächliche
              (Zeit-)Aufwand beurteilt werden. Bei Schober Transportservice
              erstellen wir Ihnen stets ein faires, realistisches Angebot und
              verhindern nachträgliche oder versteckte Kosten.
            </p>

          </div>
          <div className="overflow-hidden rounded-0 shadow-xl order-1 md:order-2">
            <img
              src={patric2}
              alt="Patric Schober"
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      <Testimonials />

      {/* Termin CTA mit Patric-2 */}
      <section className="bg-blue-950 container-fluid mx-auto ">
        <div className="grid lg:grid-cols-2 lg:items-center mx-auto = gap-0 min-h-[600px]">
          
          <div className="p-10 md:p-0 mx-5 md:mx-20">
              <h2 className="font-display text-3xl font-extrabold text-white md:text-4xl">
                Vereinbaren Sie gleich einen Termin für eine Besichtigung!
              </h2>
              <p className="mt-5 leading-relaxed text-white/90">
                Melden Sie sich noch heute für eine unverbindliche Beratung und ein
                maßgeschneidertes Angebot für Ihr Projekt. Schober Transportservice
                steht Ihnen mit Expertise, Effizienz und Zuverlässigkeit zur Seite!
              </p>
              <Link
                to="/kontakt"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-3.5 text-sm font-bold uppercase tracking-wider text-brand-orange-foreground shadow-lg transition-all hover:scale-105"
              >
                Kontakt aufnehmen <ArrowRight className="h-4 w-4" />
              </Link>
          </div>
            <div className="overflow-hidden bg-cover bg-center h-[300px] md:h-full"
            style={{ backgroundImage: `url(${team})` }}
          >

          </div>
        </div>
      </section>

      {/* Team-Bild als Abschlussband */}
      <section
        className="hidden relative h-[300px] bg-cover bg-center md:h-[420px]"
        style={{ backgroundImage: `url(${team})` }}
        aria-label="Schober Entrümpelungen Team"
      />
    </>
  );
}
